import Decimal from "decimal.js";

import { SecurityId } from "@fartherfinance/frontend/api/Types";

type SecurityList = {
  weight: Decimal;
  securities: Array<{ securityId: SecurityId; weight: Decimal }>;
};

type Category = {
  weight: Decimal;
  categories: Record<string, Category | SecurityList>;
};

export type PortfolioData = Record<string, Category>;

function countSecurities(category: Category | SecurityList): number {
  if ("securities" in category) {
    return category.securities.length;
  } else if ("categories" in category) {
    return Object.values(category.categories)
      .map(countSecurities)
      .reduce((acc, count) => acc + count, 0);
  }

  return 0;
}

export function countAllSecurities(
  data: PortfolioData
): Record<string, number> {
  return Object.entries(data).reduce((acc, [categoryName, categoryData]) => {
    return { ...acc, [categoryName]: countSecurities(categoryData) };
  }, {});
}

type CurrentSecurity = {
  type: "Securities";
  entries: { securityId: SecurityId; weight: Decimal }[];
  weight: Decimal;
};

type CurrentCategory = {
  type: "Categories";
  entries: { category: string; weight: Decimal }[];
  weight: Decimal;
};

type CurrentLevel = CurrentSecurity | CurrentCategory;

/**
 *  Recurse each element of the path looking for a key with that name in the supplied object
 */
export const getCurrentLevel = (
  data:
    | PortfolioData
    | Record<string, Category | SecurityList>
    | SecurityList
    | undefined,
  path: string[]
): CurrentLevel | null => {
  if (data === undefined) {
    return null;
  }
  if ("securities" in data) {
    if (Array.isArray(data.securities)) {
      return {
        type: "Securities",
        entries: data.securities.map((s) => ({
          securityId: s.securityId,
          weight: s.weight,
        })),
        weight: data.securities.reduce(
          (acc, { weight }) => acc.add(weight),
          new Decimal(0)
        ),
      };
    }

    throw new Error("Expected securities to be an array");
  }

  if (path.length === 0) {
    const entries = Object.entries(data).map(([key, value]) => ({
      category: key,
      weight: value.weight,
    }));

    return {
      type: "Categories",
      entries,
      weight: entries.reduce(
        (acc, { weight }) => acc.add(weight),
        new Decimal(0)
      ),
    };
  }

  const nextLevelKey = path[0];
  const nextLevel = data[nextLevelKey];
  if ("categories" in nextLevel) {
    return getCurrentLevel(nextLevel.categories, path.slice(1));
  }

  return getCurrentLevel(nextLevel, path.slice(1));
};

// Recursive function to handle nested categories
export const getSecuritiesFromCategories = (
  categories: Record<string, Category | SecurityList>,
  topCategory: string
): {
  securityId: SecurityId;
  weight: Decimal;
  topCategory: string;
}[] => {
  const categoryKeys = Object.keys(categories);

  return categoryKeys.flatMap((categoryKey) => {
    const currentCategory = categories[categoryKey];

    // If the category has securities, map them
    if ("securities" in currentCategory) {
      return currentCategory.securities.map((security) => ({
        securityId: security.securityId,
        weight: security.weight,
        topCategory,
      }));
    }

    // Otherwise, recursively search nested categories
    return getSecuritiesFromCategories(currentCategory.categories, topCategory);
  });
};

export const getAllSecurities = (
  data: PortfolioData
): {
  securityId: SecurityId;
  weight: Decimal;
  topCategory: string;
}[] => {
  const topLevelCategories = Object.keys(data);

  return topLevelCategories.flatMap((topCat) => {
    const topLevelCategory = data[topCat];
    return getSecuritiesFromCategories(topLevelCategory.categories, topCat);
  });
};
